import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import Modal from './Modal';
import { FormattedMessage, injectIntl, intlShape, defineMessages } from 'react-intl';
import { times, find } from 'lodash';
import { Link } from 'react-router';
import classnames from 'classnames';

import * as uiActions from '../../actions/ui';

import { selectOpenModalTypeIs, selectFreeCollectionModal } from '../../selectors/modals';
import { selectFeatureFlag } from '../../selectors/features';
import {
  selectAlbumCollection,
  selectPlaylistCollection,
  selectCollectionIds,
} from '../../selectors/collection';

import { createGetLimitedCollection } from '../collection/PlaylistCollection';

import Image from '../util/Image';
import List from '../util/List';

import styles from './FreeCollectionStatusModal.css';

import { ENTITY_TYPE_ALBUM, ENTITY_TYPE_PLAYLIST } from '../../constants';
import { PROFILE_IMAGE_PREVIEW_DIMS } from '../../lib/imgix';
import { compose } from 'redux';
import GetPremiumButton from '../common/GetPremiumButton';

const pluralMessages = defineMessages({
  [ENTITY_TYPE_ALBUM]: {
    id: 'collection-modal.albums',
    defaultMessage: 'albums',
  },
  [ENTITY_TYPE_PLAYLIST]: {
    id: 'collection-modal.curated-playlists',
    defaultMessage: 'curated playlists',
  },
});

const singularMessages = defineMessages({
  [ENTITY_TYPE_ALBUM]: {
    id: 'collection-modal.album',
    defaultMessage: 'album',
  },
  [ENTITY_TYPE_PLAYLIST]: {
    id: 'collection-modal.playlist',
    defaultMessage: 'playlist',
  },
});

class FreeCollectionStatusModal extends Component {
  static propTypes = {
    isOpen: PropTypes.bool.isRequired,
    hideModal: PropTypes.func.isRequired,

    collectionAccessLimit: PropTypes.number.isRequired,
    collectionLength: PropTypes.number.isRequired,
    collectionItemType: PropTypes.string.isRequired,
    limitedCollection: PropTypes.array.isRequired,
    collectionItem: PropTypes.object.isRequired,

    intl: intlShape.isRequired,
  };

  onSubscribeClick = () => {
    this.props.hideModal();
  };

  renderCollectionItem = (collectionItem, i) => {
    return (
      <li
        key={collectionItem ? collectionItem.id : i}
        className={styles.collectionItem}
        data-test={
          collectionItem ? 'free-collection-modal-item' : 'free-collection-modal-item-placeholder'
        }
      >
        <Image
          src={collectionItem ? collectionItem.imageUrl : ''}
          width={PROFILE_IMAGE_PREVIEW_DIMS.w}
          height={PROFILE_IMAGE_PREVIEW_DIMS.h}
          className={styles.bg}
          alt={collectionItem ? collectionItem.title : ''}
        />
      </li>
    );
  };

  renderHeader() {
    const {
      collectionAccessLimit,
      collectionLength,
      collectionItem,
      collectionItemType,
      limitedCollection,
      intl,
    } = this.props;

    if (collectionLength < collectionAccessLimit) {
      return (
        <span data-test="collection-modal.first-collection-addition-success-message">
          <FormattedMessage
            id="collection-modal.header.first-item"
            defaultMessage="“{title}” was saved to your Collection."
            values={{ title: collectionItem.title }}
          />
        </span>
      );
    }

    const itemWasAdded = find(limitedCollection, { id: collectionItem.id });

    if (itemWasAdded) {
      return (
        <span data-test="collection-modal.limit-reached-message">
          <FormattedMessage
            id="collection-modal.header.collection-now-full"
            defaultMessage="Your free {resourceName} Collection is now full."
            values={{
              resourceName: intl.formatMessage(singularMessages[collectionItemType]),
            }}
          />
        </span>
      );
    }

    return (
      <FormattedMessage
        id="collection-modal.header.subscribe-to-save"
        defaultMessage="Subscribe to save “{title}”."
        values={{ title: collectionItem.title }}
      />
    );
  }

  renderSubtitle() {
    const { collectionAccessLimit, collectionLength, collectionItemType, intl } = this.props;
    const resourceName = intl.formatMessage(pluralMessages[collectionItemType]);

    if (collectionLength < collectionAccessLimit) {
      return (
        <FormattedMessage
          id="collection-modal.subtitle.collection-limit-message"
          defaultMessage="You can save up to {limit} {resourceName} for free. Subscribe for an unlimited collection."
          values={{
            limit: collectionAccessLimit,
            resourceName,
          }}
        />
      );
    }
    return (
      <span data-test="collection-modal.collection-size-exceeded-message">
        <FormattedMessage
          id="collection-modal.subtitle.collection-size-exceeded-message"
          defaultMessage="When you use IDAGIO for free, you can save up to {limit} {resourceName}. Subscribe for an unlimited collection."
          values={{
            limit: collectionAccessLimit,
            resourceName,
          }}
        />
      </span>
    );
  }

  render() {
    const { isOpen, hideModal, collectionItemType, intl, limitedCollection, collectionItem } =
      this.props;
    const itemWasAdded = find(limitedCollection, { id: collectionItem.id });

    return (
      <Modal
        isOpen={isOpen}
        onRequestClose={hideModal}
        onClose={hideModal}
        contentLabel="free-collection-status-modal"
      >
        <section className={styles.component} data-test="free-collection-modal">
          {itemWasAdded ? (
            <List
              renderItem={this.renderCollectionItem}
              items={this.props.limitedCollection}
              className={styles.collectionItems}
            />
          ) : (
            <Image
              src={collectionItem ? collectionItem.imageUrl : ''}
              width={PROFILE_IMAGE_PREVIEW_DIMS.w}
              height={PROFILE_IMAGE_PREVIEW_DIMS.h}
              className={styles.img}
              alt={collectionItem ? collectionItem.title : ''}
            />
          )}
          <h1 className="fz--gamma">{this.renderHeader()}</h1>
          <p className={styles.subTitle}>{this.renderSubtitle()}</p>
          <GetPremiumButton
            className={classnames(styles.subNowButton, 'c-btn', 'c-btn--is-purple')}
            onClick={this.onSubscribeClick}
            trackingContext={{ trigger: 'freeCollectionModal' }}
            data-test="collection-modal.sub-now-btn"
          />
          {itemWasAdded && (
            <Link
              className={classnames(styles.collectionButton, 'c-btn', 'c-btn--has-outline')}
              to={`/collection/${
                collectionItemType === ENTITY_TYPE_PLAYLIST ? 'playlists' : 'albums'
              }`}
              onClick={this.props.hideModal}
              data-test="collection-modal.view-collection-btn"
            >
              <FormattedMessage
                id="collection-modal.button.view"
                defaultMessage="View {resourceName} collection"
                values={{
                  resourceName: intl.formatMessage(singularMessages[collectionItemType]),
                }}
              />
            </Link>
          )}
        </section>
      </Modal>
    );
  }
}

const getLimitedCollection = createGetLimitedCollection();

const getCollectionSelector = collectionItemType => {
  switch (collectionItemType) {
    case ENTITY_TYPE_PLAYLIST:
      return selectPlaylistCollection;
    default:
      return selectAlbumCollection;
  }
};

const getCollectionLength = (collectionItemType, collectionIds) => {
  switch (collectionItemType) {
    case ENTITY_TYPE_PLAYLIST:
      return collectionIds.entities.playlistIds.length;
    default:
      return collectionIds.entities.albumIds.length;
  }
};

function mapStateToProps(state) {
  const collectionAccessLimit = selectFeatureFlag(state, 'collections').access_limit;
  const { collectionItem, collectionItemType } = selectFreeCollectionModal(state);
  const selectCollection = getCollectionSelector(collectionItemType);

  const collection = selectCollection(state);
  const limitedCollection = getLimitedCollection(collection, collectionAccessLimit);

  const collectionIds = selectCollectionIds(state);
  const collectionLength = getCollectionLength(collectionItemType, collectionIds);

  // Fill out collection limit with placeholders
  const emptySpots = collectionAccessLimit - limitedCollection.length;
  times(emptySpots, () => limitedCollection.push(null));

  return {
    isOpen: selectOpenModalTypeIs(state, 'FREE_COLLECTION_STATUS_MODAL'),
    collectionAccessLimit: selectFeatureFlag(state, 'collections').access_limit,
    freeCollectionModal: selectFreeCollectionModal(state),
    limitedCollection,
    collectionLength,
    collectionItem, // item to be added
    collectionItemType,
  };
}

export default compose(
  connect(mapStateToProps, {
    hideModal: uiActions.hidePersonalPlaylistModal,
  }),
  injectIntl
)(FreeCollectionStatusModal);
